import { isYjsWaveFormat } from "./yjs/waveYDoc.js";
import { WaveYjsProvider } from "./yjs/yjsProvider.js";
import { WaveBlockKind, } from "./types.js";
import { shouldUseWaveYjs } from "./waveYjsConfig.js";
import { AppSyncRealtimeClient, } from "./graphql/realtimeClient.js";
import { WAVE_GRAPHQL } from "./graphql/queries.js";
import { extractWaveOpsFromSubscriptionPayload } from "./graphql/waveSubscription.js";
import { WaveCloudAPI } from "./graphql/waveApi.js";
import { hydrateWaveDocument } from "./waveHydration.js";
import { emptyWaveSyncDiagnostics } from "./waveSyncDiagnostics.js";
import { emptyWaveSaveStateMeta, } from "./waveSaveState.js";
const POLL_MS = 2500;
const SNAPSHOT_MIN_INTERVAL_MS = 10_000;
const SNAPSHOT_BATCH_THRESHOLD = 20;
const FLUSH_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 30_000];
function pendingStorageKey(waveId) {
    return `wave:pending:${waveId}`;
}
function backoffDelayMs(attempt) {
    const index = Math.min(Math.max(attempt, 0), FLUSH_BACKOFF_MS.length - 1);
    const base = FLUSH_BACKOFF_MS[index] ?? 30_000;
    const jitter = 0.75 + Math.random() * 0.5;
    return Math.round(base * jitter);
}
export class WaveSyncEngine {
    cfg;
    pendingOps = new Map();
    emitQueues = new Map();
    emitDraining = new Set();
    pollTimers = new Map();
    pollBridgeByWave = new Map();
    pendingFlushTimers = new Map();
    pendingFlushAttempt = new Map();
    snapshotThrottle = new Map();
    unsubscribeRealtime = new Map();
    realtime = null;
    activeWaveId = null;
    yjsProvider = null;
    lastEmitError = null;
    onStatusChange;
    onEmitError;
    onDocumentChange;
    onSaveStateChange;
    realtimeEndpoint;
    constructor(cfg, realtimeEndpoint, opts) {
        this.cfg = cfg;
        this.realtimeEndpoint = realtimeEndpoint;
        this.onStatusChange = opts?.onStatusChange;
        this.onEmitError = opts?.onEmitError;
        this.onDocumentChange = opts?.onDocumentChange;
        this.onSaveStateChange = opts?.onSaveStateChange;
        this.realtime = new AppSyncRealtimeClient({
            graphqlEndpoint: cfg.endpoint,
            realtimeEndpoint,
            getIdToken: cfg.getIdToken,
            // notifyRealtimeConnectionChange riconcilia già sulla transizione a connesso.
            onConnectionChange: () => {
                this.yjsProvider?.notifyRealtimeConnectionChange();
                this.onStatusChange?.(this.realtime?.getStatus() ?? "offline");
            },
            onStatusChange: (status) => this.onStatusChange?.(status),
        });
    }
    getLastEmitError() {
        return this.lastEmitError;
    }
    getStatus() {
        return this.realtime?.getStatus() ?? "offline";
    }
    getYjsProvider() {
        return this.yjsProvider;
    }
    usesYjs() {
        return this.yjsProvider != null;
    }
    getDocumentEditor() {
        return this.yjsProvider?.editor ?? null;
    }
    getDocumentBlocks() {
        if (this.yjsProvider)
            return this.yjsProvider.blocks;
        return [];
    }
    getDocumentRevision() {
        return this.yjsProvider?.contentRevision ?? 0;
    }
    isNetworkHydrating() {
        return this.yjsProvider?.isNetworkHydrating() ?? false;
    }
    subscribeNetworkHydration(listener) {
        if (!this.yjsProvider)
            return () => { };
        return this.yjsProvider.subscribeNetworkHydration(listener);
    }
    getSyncDiagnostics() {
        return this.yjsProvider?.getSyncDiagnostics() ?? emptyWaveSyncDiagnostics();
    }
    subscribeSyncDiagnostics(listener) {
        if (!this.yjsProvider)
            return () => { };
        return this.yjsProvider.subscribeSyncDiagnostics(listener);
    }
    getSaveState() {
        return this.yjsProvider?.getSaveState() ?? "synced";
    }
    getSaveStateMeta() {
        return this.yjsProvider?.getSaveStateMeta() ?? emptyWaveSaveStateMeta();
    }
    notifyBrowserOnline() {
        this.yjsProvider?.notifyBrowserOnline();
    }
    notifyBrowserOffline() {
        this.yjsProvider?.notifyBrowserOffline();
    }
    async loadWave(bridge, wave) {
        if (this.activeWaveId === wave.waveId) {
            return;
        }
        if (this.activeWaveId && this.activeWaveId !== wave.waveId) {
            this.unloadWave(this.activeWaveId);
        }
        this.activeWaveId = wave.waveId;
        if (shouldUseWaveYjs(wave.formatVersion)) {
            this.yjsProvider?.dispose();
            this.yjsProvider = new WaveYjsProvider(this.cfg, this.realtime, {
                onStatusChange: this.onStatusChange,
                onEmitError: this.onEmitError,
                onLocalHydrated: () => this.onDocumentChange?.(),
                onSaveStateChange: this.onSaveStateChange,
            });
            await this.yjsProvider.connect(wave);
            if (!isYjsWaveFormat(wave.formatVersion)) {
                const blocks = this.yjsProvider.blocks;
                const canUpgrade = wave.nextOpSeq <= 0 ||
                    blocks.some((b) => (b.kind === WaveBlockKind.Text &&
                        (b.payload?.text ?? "").trim().length > 0) ||
                        b.kind !== WaveBlockKind.Text);
                if (canUpgrade) {
                    const seq = Math.max(wave.latestSnapshotSeq, this.yjsProvider.appliedOpSeq, wave.nextOpSeq > 0 ? wave.nextOpSeq - 1 : 0);
                    await this.yjsProvider.upgradeToYjsV1(seq);
                }
            }
            const unsub = this.yjsProvider.subscribe(() => {
                this.onDocumentChange?.();
                this.yjsProvider?.notifyDocumentMaterialized();
            });
            this.unsubscribeRealtime.set(wave.waveId, unsub);
            return;
        }
        this.yjsProvider?.dispose();
        this.yjsProvider = null;
        this.pollBridgeByWave.set(wave.waveId, bridge);
        this.restorePendingOps(wave.waveId);
        await hydrateWaveDocument(this.cfg, bridge, wave);
        await this.startRealtime(wave.waveId, bridge);
    }
    /** Re-fetch cloud document into the open wave without changing activeWaveId. */
    async rehydrateDocument(bridge, wave) {
        if (this.activeWaveId !== wave.waveId)
            return;
        if (shouldUseWaveYjs(wave.formatVersion)) {
            this.unsubscribeRealtime.get(wave.waveId)?.();
            if (this.yjsProvider) {
                await this.yjsProvider.reloadFromCloud(wave);
            }
            else {
                this.yjsProvider = new WaveYjsProvider(this.cfg, this.realtime, {
                    onStatusChange: this.onStatusChange,
                    onEmitError: this.onEmitError,
                    onSaveStateChange: this.onSaveStateChange,
                });
                await this.yjsProvider.connect(wave);
            }
            const unsub = this.yjsProvider.subscribe(() => {
                this.onDocumentChange?.();
                this.yjsProvider?.notifyDocumentMaterialized();
            });
            this.unsubscribeRealtime.set(wave.waveId, unsub);
            return;
        }
        await hydrateWaveDocument(this.cfg, bridge, wave);
    }
    unloadWave(waveId) {
        if (this.yjsProvider && this.activeWaveId === waveId) {
            this.yjsProvider.dispose();
            this.yjsProvider = null;
        }
        this.stopPolling(waveId);
        this.pollBridgeByWave.delete(waveId);
        this.clearPendingFlushTimer(waveId);
        this.pendingFlushAttempt.delete(waveId);
        this.snapshotThrottle.delete(waveId);
        this.unsubscribeRealtime.get(waveId)?.();
        this.unsubscribeRealtime.delete(waveId);
        this.emitQueues.delete(waveId);
        this.emitDraining.delete(waveId);
        if (this.activeWaveId === waveId) {
            this.activeWaveId = null;
        }
        this.updatePollingFallbackStatus();
    }
    dispose() {
        this.yjsProvider?.dispose();
        this.yjsProvider = null;
        for (const waveId of [...this.pollTimers.keys()]) {
            this.unloadWave(waveId);
        }
        this.realtime?.disconnect();
        this.realtime = null;
    }
    /** Push pending Yjs local diff to cloud (call after structural doc edits). */
    async flushDocumentSync(waveId) {
        if (this.yjsProvider && this.activeWaveId === waveId) {
            await this.yjsProvider.flushLocalUpdateNow();
        }
    }
    /**
     * Synchronous teardown before full-page unload (e.g. Safari refresh).
     * Closes WebSocket and persists Yjs state without awaiting network.
     */
    prepareForUnload(waveId) {
        if (this.yjsProvider && this.activeWaveId === waveId) {
            this.yjsProvider.prepareForUnload();
        }
        this.realtime?.disconnect();
        this.stopPolling(waveId);
    }
    emitOps(waveId, bridge, opsB64) {
        if (!opsB64.length)
            return;
        if (this.yjsProvider && this.activeWaveId === waveId) {
            void this.yjsProvider.appendOpsB64(opsB64);
            return;
        }
        const queue = this.emitQueues.get(waveId) ?? [];
        queue.push(...opsB64);
        this.emitQueues.set(waveId, queue);
        void this.drainEmit(waveId, bridge);
    }
    getPendingOps(waveId) {
        return this.pendingOps.get(waveId) ?? [];
    }
    setEmitError(message) {
        this.lastEmitError = message;
        this.onEmitError?.(message);
    }
    persistPendingOps(waveId) {
        const ops = this.pendingOps.get(waveId);
        if (ops?.length) {
            sessionStorage.setItem(pendingStorageKey(waveId), JSON.stringify(ops));
            return;
        }
        sessionStorage.removeItem(pendingStorageKey(waveId));
    }
    restorePendingOps(waveId) {
        const raw = sessionStorage.getItem(pendingStorageKey(waveId));
        if (!raw)
            return;
        try {
            const ops = JSON.parse(raw);
            if (ops.length) {
                const existing = this.pendingOps.get(waveId) ?? [];
                this.pendingOps.set(waveId, [...ops, ...existing]);
            }
        }
        catch {
            /* ignore malformed */
        }
        sessionStorage.removeItem(pendingStorageKey(waveId));
    }
    clearPendingFlushTimer(waveId) {
        const timer = this.pendingFlushTimers.get(waveId);
        if (timer) {
            clearTimeout(timer);
            this.pendingFlushTimers.delete(waveId);
        }
    }
    schedulePendingRetry(waveId, bridge) {
        this.clearPendingFlushTimer(waveId);
        const attempt = (this.pendingFlushAttempt.get(waveId) ?? 0) + 1;
        this.pendingFlushAttempt.set(waveId, attempt);
        const timer = setTimeout(() => {
            this.pendingFlushTimers.delete(waveId);
            void this.flushPending(waveId, bridge);
        }, backoffDelayMs(attempt - 1));
        this.pendingFlushTimers.set(waveId, timer);
    }
    maybePublishSnapshot(waveId, bridge, lastSeq) {
        if (lastSeq <= 0)
            return Promise.resolve();
        const snap = bridge.exportSnapshotB64();
        if (!snap)
            return Promise.resolve();
        const state = this.snapshotThrottle.get(waveId) ?? {
            lastAt: 0,
            batchesSince: 0,
        };
        state.batchesSince += 1;
        const now = Date.now();
        const shouldPublish = now - state.lastAt >= SNAPSHOT_MIN_INTERVAL_MS ||
            state.batchesSince >= SNAPSHOT_BATCH_THRESHOLD;
        if (!shouldPublish) {
            this.snapshotThrottle.set(waveId, state);
            return Promise.resolve();
        }
        state.lastAt = now;
        state.batchesSince = 0;
        this.snapshotThrottle.set(waveId, state);
        return WaveCloudAPI.publishSnapshot(this.cfg, waveId, snap, lastSeq).catch(() => {
            /* snapshot best effort */
        });
    }
    async drainEmit(waveId, bridge) {
        if (this.emitDraining.has(waveId))
            return;
        this.emitDraining.add(waveId);
        try {
            while (true) {
                const batch = this.emitQueues.get(waveId);
                if (!batch?.length)
                    break;
                this.emitQueues.set(waveId, []);
                try {
                    const result = await WaveCloudAPI.appendOps(this.cfg, waveId, batch);
                    this.setEmitError(null);
                    this.pendingFlushAttempt.set(waveId, 0);
                    this.clearPendingFlushTimer(waveId);
                    if (result.ops.length) {
                        bridge.markOpsSeen(result.ops);
                    }
                    const lastSeq = Math.max(...result.ops.map((o) => o.seq), 0);
                    await this.maybePublishSnapshot(waveId, bridge, lastSeq);
                }
                catch (err) {
                    const message = err instanceof Error ? err.message : "Sync wave non riuscita";
                    this.setEmitError(message);
                    const pending = this.pendingOps.get(waveId) ?? [];
                    pending.unshift(...batch);
                    this.pendingOps.set(waveId, pending);
                    this.persistPendingOps(waveId);
                    this.schedulePendingRetry(waveId, bridge);
                    break;
                }
            }
        }
        finally {
            this.emitDraining.delete(waveId);
            if ((this.emitQueues.get(waveId)?.length ?? 0) > 0) {
                void this.drainEmit(waveId, bridge);
            }
        }
    }
    async flushPending(waveId, bridge) {
        const queued = this.pendingOps.get(waveId);
        if (!queued?.length)
            return;
        this.pendingOps.set(waveId, []);
        this.persistPendingOps(waveId);
        this.emitOps(waveId, bridge, queued);
    }
    async startRealtime(waveId, bridge) {
        if (!this.realtime)
            return;
        try {
            await this.realtime.connect();
        }
        catch {
            this.startPolling(waveId, bridge);
            return;
        }
        this.unsubscribeRealtime.get(waveId)?.();
        const unsub = this.realtime.subscribe(WAVE_GRAPHQL.onWaveOps, { waveId }, (batch) => {
            const ops = extractWaveOpsFromSubscriptionPayload(batch);
            if (ops.length) {
                bridge.applyRemoteOps(ops);
            }
            void this.flushPending(waveId, bridge);
        });
        this.unsubscribeRealtime.set(waveId, unsub);
        this.startPolling(waveId, bridge);
    }
    startPolling(waveId, bridge) {
        if (this.pollTimers.has(waveId))
            return;
        this.pollBridgeByWave.set(waveId, bridge);
        const timer = setInterval(() => {
            void (async () => {
                try {
                    const ops = await WaveCloudAPI.listAllOps(this.cfg, waveId, bridge.appliedSeq);
                    if (ops.length)
                        bridge.applyRemoteOps(ops);
                    await this.flushPending(waveId, bridge);
                }
                catch (err) {
                    const message = err instanceof Error ? err.message : "Sync polling non riuscito";
                    this.setEmitError(message);
                }
            })();
        }, POLL_MS);
        this.pollTimers.set(waveId, timer);
        this.updatePollingFallbackStatus();
    }
    stopPolling(waveId) {
        const t = this.pollTimers.get(waveId);
        if (t) {
            clearInterval(t);
            this.pollTimers.delete(waveId);
        }
        this.updatePollingFallbackStatus();
    }
    updatePollingFallbackStatus() {
        this.realtime?.setPollingFallbackActive(this.pollTimers.size > 0);
    }
    subscribeStatus(waveId, handler) {
        if (!this.realtime)
            return () => { };
        return this.realtime.subscribe(WAVE_GRAPHQL.onWaveStatusChanged, { waveId }, (w) => {
            if (w)
                handler(w);
        });
    }
    subscribeBlip(waveId, handler) {
        if (!this.realtime)
            return () => { };
        return this.realtime.subscribe(WAVE_GRAPHQL.onWaveBlip, { waveId }, handler);
    }
    subscribePresence(waveId, handler) {
        if (!this.realtime)
            return () => { };
        return this.realtime.subscribe(WAVE_GRAPHQL.onWavePresence, { waveId }, handler);
    }
    subscribeChatMessage(waveId, handler) {
        if (!this.realtime)
            return () => { };
        return this.realtime.subscribe(WAVE_GRAPHQL.onWaveChatMessage, { waveId }, handler);
    }
    subscribeChatReaction(waveId, handler) {
        if (!this.realtime)
            return () => { };
        return this.realtime.subscribe(WAVE_GRAPHQL.onWaveChatReaction, { waveId }, handler);
    }
}
